import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, Bell, Trophy, DollarSign, Grid3X3, Tag, Users, Edit2, X, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AdminReferralManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('tiers');
  const [tiers, setTiers] = useState([]);
  const [matrix, setMatrix] = useState([]);
  const [codes, setCodes] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderStats, setLeaderStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingTier, setEditingTier] = useState(null);
  const [editingCode, setEditingCode] = useState(null);
  const [plans, setPlans] = useState([]);

  const headers = useCallback(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }, []);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const h = headers();
      const [tiersRes, matrixRes, codesRes, notifsRes, leaderRes, plansRes] = await Promise.all([
        fetch(`${API}/api/admin/referral/tiers`, { headers: h }),
        fetch(`${API}/api/admin/referral/points-matrix`, { headers: h }),
        fetch(`${API}/api/admin/referral/discount-codes`, { headers: h }),
        fetch(`${API}/api/admin/referral/notifications`, { headers: h }),
        fetch(`${API}/api/referral/admin/leaderboard`, { headers: h }),
        fetch(`${API}/api/admin/subscription-plans`, { headers: h }),
      ]);
      if (tiersRes.ok) { const d = await tiersRes.json(); setTiers(d.tiers || []); }
      if (matrixRes.ok) { const d = await matrixRes.json(); setMatrix(d.matrix || []); }
      if (codesRes.ok) { const d = await codesRes.json(); setCodes(d.codes || []); }
      if (notifsRes.ok) { const d = await notifsRes.json(); setNotifications(d.notifications || []); setUnreadCount(d.unread_count || 0); }
      if (leaderRes.ok) { const d = await leaderRes.json(); setLeaderboard(d.leaderboard || []); setLeaderStats(d.stats || {}); }
      if (plansRes.ok) { const d = await plansRes.json(); setPlans(Array.isArray(d) ? d : []); }
    } catch (err) { /* ignore */ }
    finally { setLoading(false); }
  };

  // === TIERS ===
  const defaultTier = { name: '', icon: '', points_required: 0, color: 'amber', free_rewards: { extra_hives: 0, features: [] }, paid_rewards: { discount_code: '', description: '' } };

  const saveTier = async (tier) => {
    const h = headers();
    try {
      if (tier.id) {
        await fetch(`${API}/api/admin/referral/tiers/${tier.id}`, { method: 'PUT', headers: h, body: JSON.stringify(tier) });
      } else {
        await fetch(`${API}/api/admin/referral/tiers`, { method: 'POST', headers: h, body: JSON.stringify(tier) });
      }
      toast.success('Tier saved');
      setEditingTier(null);
      fetchAll();
    } catch { toast.error('Failed to save tier'); }
  };

  const deleteTier = async (id) => {
    if (!confirm('Delete this tier?')) return;
    await fetch(`${API}/api/admin/referral/tiers/${id}`, { method: 'DELETE', headers: headers() });
    toast.success('Tier deleted');
    fetchAll();
  };

  // === MATRIX ===
  const planOptions = [
    { id: 'free', label: 'Free' },
    ...plans.filter(p => p.price_monthly > 0).map(p => ({ id: p.plan_id, label: `${p.name} ($${p.price_monthly}/mo)` }))
  ];

  const getMatrixPoints = (referrer, referred) => {
    const entry = matrix.find(m => m.referrer_plan === referrer && m.referred_plan === referred);
    return entry ? entry.points : 1;
  };

  const updateMatrixCell = async (referrer, referred, points) => {
    const h = headers();
    await fetch(`${API}/api/admin/referral/points-matrix`, {
      method: 'PUT', headers: h,
      body: JSON.stringify({ matrix: [{ referrer_plan: referrer, referred_plan: referred, points: parseInt(points) || 1 }] })
    });
    fetchAll();
  };

  // === CODES ===
  const defaultCode = { code: '', description: '', discount_type: 'percentage', discount_value: 10, usage_type: 'bulk', tier_id: '', max_uses: 0 };

  const saveCode = async (code) => {
    const h = headers();
    try {
      if (code.id) {
        await fetch(`${API}/api/admin/referral/discount-codes/${code.id}`, { method: 'PUT', headers: h, body: JSON.stringify(code) });
      } else {
        await fetch(`${API}/api/admin/referral/discount-codes`, { method: 'POST', headers: h, body: JSON.stringify(code) });
      }
      toast.success('Code saved');
      setEditingCode(null);
      fetchAll();
    } catch { toast.error('Failed to save'); }
  };

  const deleteCode = async (id) => {
    if (!confirm('Delete this code?')) return;
    await fetch(`${API}/api/admin/referral/discount-codes/${id}`, { method: 'DELETE', headers: headers() });
    toast.success('Code deleted');
    fetchAll();
  };

  const markNotifRead = async (id) => {
    await fetch(`${API}/api/admin/notifications/${id}/read`, { method: 'PUT', headers: headers() });
    fetchAll();
  };

  const tabs = [
    { id: 'tiers', label: 'Reward Tiers', icon: Trophy },
    { id: 'matrix', label: 'Points Matrix', icon: Grid3X3 },
    { id: 'codes', label: 'Discount Codes', icon: Tag },
    { id: 'leaderboard', label: 'Leaderboard', icon: Users },
    { id: 'notifications', label: 'Notifications', icon: Bell, badge: unreadCount },
  ];

  const featureOptions = ['Badge on Profile', 'Swarm Management', 'Split Management', 'Extra Apiary', 'AI Features', 'Queen Rearing', 'Priority Support', 'Analytics', 'Marketplace Selling', 'Extra Photos', 'Extra Videos'];

  if (loading) return <div className="min-h-screen honeycomb-bg flex items-center justify-center"><div className="text-stone-500">Loading...</div></div>;

  return (
    <div className="min-h-screen honeycomb-bg pb-24">
      <div className="max-w-6xl mx-auto p-4 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-stone-100 rounded-lg"><ArrowLeft className="h-5 w-5" /></button>
          <div>
            <h1 className="text-2xl font-bold text-stone-900">Referral Program Management</h1>
            <p className="text-sm text-stone-500">Configure tiers, points, discount codes & partnerships</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-xl p-1 border border-stone-200 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id ? 'bg-amber-500 text-white' : 'text-stone-600 hover:bg-stone-100'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
              {tab.badge > 0 && <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">{tab.badge}</span>}
            </button>
          ))}
        </div>

        {/* TIERS TAB */}
        {activeTab === 'tiers' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="font-bold text-stone-800">Reward Tiers</h2>
              <Button onClick={() => setEditingTier({ ...defaultTier })} className="bg-amber-500 hover:bg-amber-600">
                <Plus className="h-4 w-4 mr-1" /> Add Tier
              </Button>
            </div>

            {editingTier && (
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-4 space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="font-bold text-amber-800">{editingTier.id ? 'Edit' : 'New'} Tier</h3>
                    <button onClick={() => setEditingTier(null)}><X className="h-4 w-4" /></button>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div>
                      <label className="text-xs text-stone-500">Name</label>
                      <Input value={editingTier.name} onChange={e => setEditingTier(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Pollinator" />
                    </div>
                    <div>
                      <label className="text-xs text-stone-500">Icon (emoji)</label>
                      <Input value={editingTier.icon} onChange={e => setEditingTier(p => ({ ...p, icon: e.target.value }))} placeholder="e.g. 🌻" />
                    </div>
                    <div>
                      <label className="text-xs text-stone-500">Points Required</label>
                      <Input type="number" value={editingTier.points_required} onChange={e => setEditingTier(p => ({ ...p, points_required: parseInt(e.target.value) || 0 }))} />
                    </div>
                    <div>
                      <label className="text-xs text-stone-500">Color</label>
                      <select value={editingTier.color} onChange={e => setEditingTier(p => ({ ...p, color: e.target.value }))} className="w-full h-9 border rounded-md px-2 text-sm">
                        {['green', 'yellow', 'amber', 'purple', 'blue', 'teal'].map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-3 border">
                      <h4 className="text-sm font-bold text-stone-700 mb-2">Free User Rewards</h4>
                      <div className="space-y-2">
                        <div>
                          <label className="text-xs text-stone-500">Extra Hives</label>
                          <Input type="number" value={editingTier.free_rewards?.extra_hives || 0} onChange={e => setEditingTier(p => ({ ...p, free_rewards: { ...p.free_rewards, extra_hives: parseInt(e.target.value) || 0 } }))} />
                        </div>
                        <div>
                          <label className="text-xs text-stone-500">Extra Apiaries</label>
                          <Input type="number" value={editingTier.free_rewards?.extra_apiaries || 0} onChange={e => setEditingTier(p => ({ ...p, free_rewards: { ...p.free_rewards, extra_apiaries: parseInt(e.target.value) || 0 } }))} />
                        </div>
                        <div>
                          <label className="text-xs text-stone-500">Extra Photos/Inspection</label>
                          <Input type="number" value={editingTier.free_rewards?.extra_photos || 0} onChange={e => setEditingTier(p => ({ ...p, free_rewards: { ...p.free_rewards, extra_photos: parseInt(e.target.value) || 0 } }))} />
                        </div>
                        <div>
                          <label className="text-xs text-stone-500">Extra Videos/Inspection</label>
                          <Input type="number" value={editingTier.free_rewards?.extra_videos || 0} onChange={e => setEditingTier(p => ({ ...p, free_rewards: { ...p.free_rewards, extra_videos: parseInt(e.target.value) || 0 } }))} />
                        </div>
                        <div>
                          <label className="text-xs text-stone-500">Unlock Features</label>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {featureOptions.map(f => (
                              <button key={f} onClick={() => {
                                const features = editingTier.free_rewards?.features || [];
                                const updated = features.includes(f) ? features.filter(x => x !== f) : [...features, f];
                                setEditingTier(p => ({ ...p, free_rewards: { ...p.free_rewards, features: updated } }));
                              }}
                              className={`text-xs px-2 py-1 rounded-full border ${(editingTier.free_rewards?.features || []).includes(f) ? 'bg-green-100 border-green-300 text-green-700' : 'bg-stone-50 border-stone-200 text-stone-500'}`}>
                                {f}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-3 border">
                      <h4 className="text-sm font-bold text-stone-700 mb-2">Paid User Rewards</h4>
                      <div className="space-y-2">
                        <div>
                          <label className="text-xs text-stone-500">Discount Code (auto-assign)</label>
                          <Input value={editingTier.paid_rewards?.discount_code || ''} onChange={e => setEditingTier(p => ({ ...p, paid_rewards: { ...p.paid_rewards, discount_code: e.target.value } }))} placeholder="e.g. POLLINATOR-10OFF" />
                        </div>
                        <div>
                          <label className="text-xs text-stone-500">Reward Description</label>
                          <Input value={editingTier.paid_rewards?.description || ''} onChange={e => setEditingTier(p => ({ ...p, paid_rewards: { ...p.paid_rewards, description: e.target.value } }))} placeholder="e.g. 10% off next renewal" />
                        </div>
                      </div>
                    </div>
                  </div>

                  <Button onClick={() => saveTier(editingTier)} className="bg-green-600 hover:bg-green-700">
                    <Save className="h-4 w-4 mr-1" /> Save Tier
                  </Button>
                </CardContent>
              </Card>
            )}

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {tiers.map(tier => (
                <Card key={tier.id} className="bg-white">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{tier.icon}</span>
                        <div>
                          <h3 className="font-bold text-stone-800">{tier.name}</h3>
                          <p className="text-xs text-stone-500">{tier.points_required} points</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button onClick={() => setEditingTier({ ...tier })} className="p-1.5 hover:bg-stone-100 rounded"><Edit2 className="h-3.5 w-3.5" /></button>
                        <button onClick={() => deleteTier(tier.id)} className="p-1.5 hover:bg-red-50 text-red-500 rounded"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                    {tier.free_rewards && (
                      <div className="bg-green-50 rounded-lg p-2 mb-2">
                        <p className="text-xs font-bold text-green-700 mb-1">Free User Rewards</p>
                        {tier.free_rewards.extra_hives > 0 && <p className="text-xs text-green-600">+{tier.free_rewards.extra_hives} hives</p>}
                        {tier.free_rewards.extra_apiaries > 0 && <p className="text-xs text-green-600">+{tier.free_rewards.extra_apiaries} apiaries</p>}
                        {tier.free_rewards.features?.length > 0 && <p className="text-xs text-green-600">{tier.free_rewards.features.join(', ')}</p>}
                      </div>
                    )}
                    {tier.paid_rewards?.description && (
                      <div className="bg-purple-50 rounded-lg p-2">
                        <p className="text-xs font-bold text-purple-700 mb-1">Paid User Rewards</p>
                        <p className="text-xs text-purple-600">{tier.paid_rewards.description}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              {tiers.length === 0 && <p className="text-stone-400 col-span-full text-center py-8">No tiers configured yet. Click "Add Tier" to create one.</p>}
            </div>
          </div>
        )}

        {/* POINTS MATRIX TAB */}
        {activeTab === 'matrix' && (
          <div className="space-y-4">
            <h2 className="font-bold text-stone-800">Points Matrix</h2>
            <p className="text-sm text-stone-500">Set how many points a referrer earns based on both their plan and the referred person's plan.</p>
            <Card className="bg-white overflow-x-auto">
              <CardContent className="p-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3 text-stone-500">Referrer \ Referred</th>
                      {planOptions.map(p => <th key={p.id} className="py-2 px-3 text-stone-700 text-center">{p.label}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {planOptions.map(referrer => (
                      <tr key={referrer.id} className="border-b last:border-0">
                        <td className="py-2 px-3 font-medium text-stone-700">{referrer.label}</td>
                        {planOptions.map(referred => (
                          <td key={referred.id} className="py-2 px-3 text-center">
                            <input
                              type="number"
                              min="1"
                              className="w-16 text-center border rounded px-2 py-1 text-sm"
                              defaultValue={getMatrixPoints(referrer.id, referred.id)}
                              onBlur={e => {
                                const val = parseInt(e.target.value) || 1;
                                if (val !== getMatrixPoints(referrer.id, referred.id)) {
                                  updateMatrixCell(referrer.id, referred.id, val);
                                }
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-xs text-stone-400 mt-3">Changes save automatically when you click away from a cell.</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* DISCOUNT CODES TAB */}
        {activeTab === 'codes' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="font-bold text-stone-800">Discount Codes</h2>
              <Button onClick={() => setEditingCode({ ...defaultCode })} className="bg-amber-500 hover:bg-amber-600">
                <Plus className="h-4 w-4 mr-1" /> Add Code
              </Button>
            </div>

            {editingCode && (
              <Card className="bg-amber-50 border-amber-200">
                <CardContent className="p-4 space-y-3">
                  <div className="flex justify-between"><h3 className="font-bold text-amber-800">{editingCode.id ? 'Edit' : 'New'} Code</h3><button onClick={() => setEditingCode(null)}><X className="h-4 w-4" /></button></div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div><label className="text-xs text-stone-500">Code</label><Input value={editingCode.code} onChange={e => setEditingCode(p => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="SPRING10" /></div>
                    <div><label className="text-xs text-stone-500">Description</label><Input value={editingCode.description} onChange={e => setEditingCode(p => ({ ...p, description: e.target.value }))} /></div>
                    <div><label className="text-xs text-stone-500">Type</label>
                      <select value={editingCode.discount_type} onChange={e => setEditingCode(p => ({ ...p, discount_type: e.target.value }))} className="w-full h-9 border rounded-md px-2 text-sm">
                        <option value="percentage">Percentage (%)</option><option value="fixed">Fixed ($)</option>
                      </select>
                    </div>
                    <div><label className="text-xs text-stone-500">Value</label><Input type="number" value={editingCode.discount_value} onChange={e => setEditingCode(p => ({ ...p, discount_value: parseFloat(e.target.value) || 0 }))} /></div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div><label className="text-xs text-stone-500">Usage</label>
                      <select value={editingCode.usage_type} onChange={e => setEditingCode(p => ({ ...p, usage_type: e.target.value }))} className="w-full h-9 border rounded-md px-2 text-sm">
                        <option value="bulk">Bulk (mass marketing)</option><option value="tier">Tier reward (auto-assigned)</option>
                      </select>
                    </div>
                    {editingCode.usage_type === 'tier' && (
                      <div><label className="text-xs text-stone-500">Linked Tier</label>
                        <select value={editingCode.tier_id} onChange={e => setEditingCode(p => ({ ...p, tier_id: e.target.value }))} className="w-full h-9 border rounded-md px-2 text-sm">
                          <option value="">Select tier...</option>
                          {tiers.map(t => <option key={t.id} value={t.id}>{t.name} ({t.points_required} pts)</option>)}
                        </select>
                      </div>
                    )}
                    <div><label className="text-xs text-stone-500">Max Uses (0=unlimited)</label><Input type="number" value={editingCode.max_uses} onChange={e => setEditingCode(p => ({ ...p, max_uses: parseInt(e.target.value) || 0 }))} /></div>
                  </div>
                  <Button onClick={() => saveCode(editingCode)} className="bg-green-600 hover:bg-green-700"><Save className="h-4 w-4 mr-1" /> Save</Button>
                </CardContent>
              </Card>
            )}

            <div className="space-y-2">
              {codes.map(code => (
                <Card key={code.id} className="bg-white">
                  <CardContent className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <code className="bg-amber-100 text-amber-800 px-3 py-1 rounded-lg font-mono font-bold text-sm">{code.code}</code>
                      <span className="text-sm text-stone-600">{code.description}</span>
                      <span className="text-xs bg-stone-100 px-2 py-0.5 rounded">{code.discount_value}{code.discount_type === 'percentage' ? '%' : '$'} off</span>
                      <span className="text-xs text-stone-400">{code.usage_type === 'bulk' ? 'Bulk' : 'Tier'}</span>
                      <span className="text-xs text-stone-400">Used: {code.uses_count || 0}{code.max_uses > 0 ? `/${code.max_uses}` : ''}</span>
                      <span className={`text-xs px-2 py-0.5 rounded ${code.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{code.is_active ? 'Active' : 'Inactive'}</span>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => setEditingCode({ ...code })} className="p-1.5 hover:bg-stone-100 rounded"><Edit2 className="h-3.5 w-3.5" /></button>
                      <button onClick={() => deleteCode(code.id)} className="p-1.5 hover:bg-red-50 text-red-500 rounded"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {codes.length === 0 && <p className="text-stone-400 text-center py-8">No discount codes yet.</p>}
            </div>
          </div>
        )}

        {/* LEADERBOARD TAB */}
        {activeTab === 'leaderboard' && (
          <div className="space-y-4">
            <h2 className="font-bold text-stone-800">Referral Leaderboard</h2>
            <div className="grid grid-cols-3 gap-4">
              <Card className="bg-white"><CardContent className="p-4 text-center"><div className="text-3xl font-black text-amber-600">{leaderStats.total || 0}</div><div className="text-xs text-stone-500">Total Referrals</div></CardContent></Card>
              <Card className="bg-white"><CardContent className="p-4 text-center"><div className="text-3xl font-black text-green-600">{leaderStats.confirmed || 0}</div><div className="text-xs text-stone-500">Confirmed</div></CardContent></Card>
              <Card className="bg-white"><CardContent className="p-4 text-center"><div className="text-3xl font-black text-blue-600">{leaderStats.pending || 0}</div><div className="text-xs text-stone-500">Pending</div></CardContent></Card>
            </div>
            <Card className="bg-white">
              <CardContent className="p-0">
                <table className="w-full text-sm">
                  <thead><tr className="bg-stone-50 border-b"><th className="text-left py-3 px-4">#</th><th className="text-left py-3 px-4">User</th><th className="text-left py-3 px-4">Email</th><th className="text-left py-3 px-4">Company</th><th className="text-center py-3 px-4">Referrals</th><th className="text-left py-3 px-4">Latest</th></tr></thead>
                  <tbody>
                    {leaderboard.map((row, i) => (
                      <tr key={row.user_id} className="border-b last:border-0 hover:bg-stone-50">
                        <td className="py-3 px-4 font-bold text-amber-600">{i + 1}</td>
                        <td className="py-3 px-4 font-medium">{row.name}</td>
                        <td className="py-3 px-4 text-stone-500">{row.email}</td>
                        <td className="py-3 px-4 text-stone-500">{row.company}</td>
                        <td className="py-3 px-4 text-center font-bold">{row.total_referrals}</td>
                        <td className="py-3 px-4 text-stone-400 text-xs">{row.latest_referral ? new Date(row.latest_referral).toLocaleDateString() : ''}</td>
                      </tr>
                    ))}
                    {leaderboard.length === 0 && <tr><td colSpan="6" className="py-8 text-center text-stone-400">No referrals yet.</td></tr>}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </div>
        )}

        {/* NOTIFICATIONS TAB */}
        {activeTab === 'notifications' && (
          <div className="space-y-4">
            <h2 className="font-bold text-stone-800">Referral Notifications</h2>
            <div className="space-y-2">
              {notifications.map(n => (
                <Card key={n.id} className={`${n.is_read ? 'bg-white' : 'bg-amber-50 border-amber-200'}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        {!n.is_read && <span className="h-2 w-2 rounded-full bg-amber-500" />}
                        <h3 className="font-medium text-stone-800">{n.title}</h3>
                      </div>
                      <p className="text-sm text-stone-500 mt-0.5">{n.message}</p>
                      <p className="text-xs text-stone-400 mt-1">{n.created_at ? new Date(n.created_at).toLocaleString() : ''}</p>
                    </div>
                    {!n.is_read && (
                      <Button variant="ghost" size="sm" onClick={() => markNotifRead(n.id)}>
                        <Check className="h-4 w-4 mr-1" /> Mark Read
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
              {notifications.length === 0 && <p className="text-stone-400 text-center py-8">No referral notifications yet. You'll be alerted when users hit reward tiers.</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReferralManagement;
